import { Plus, X } from 'lucide-react';
import { NAVY } from '../utils/constants';
import { Button } from './ui';
import { lineTotal, lineTotals, fmtMoney } from '../utils/eventLines';

// ─── Line items editor ───────────────────────────────────────────────────────
// A row per line: title, amount per unit, quantity, tax %, with its own total;
// below, taxable value, tax and the grand total. The agreed total is these
// lines and nothing else, so there is no total field to drift from them.
// Working-out for staff, not a document.

export const blankLine = () => ({ id: null, title: '', unit_amount: '', quantity: '1', tax_percent: '0' });
export const isBlankLine = (l) => !l.title.trim() && l.unit_amount === '';

const head = { fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.4px' };
const num = { textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

export default function LineItemsEditor({ lines, onChange, errors = {} }) {
  const set = (i, key) => (e) => onChange(lines.map((l, j) => (j === i ? { ...l, [key]: e.target.value } : l)));
  const remove = (i) => onChange(lines.filter((_, j) => j !== i));
  const add = () => onChange([...lines, blankLine()]);
  const totals = lineTotals(lines.filter(l => !isBlankLine(l)));

  return (
    <div className="ev-lines">
      {lines.length > 0 && (
        <div className="ev-line ev-line-head" aria-hidden="true">
          <span style={head}>Item</span>
          <span style={{ ...head, ...num }}>Amount</span>
          <span style={{ ...head, ...num }}>Qty</span>
          <span style={{ ...head, ...num }}>Tax %</span>
          <span style={{ ...head, ...num }}>Line total</span>
          <span />
        </div>
      )}
      {lines.map((l, i) => {
        const err = errors[i] || {};
        return (
          <div className="ev-line" key={l.id || `new-${i}`}>
            <div className="ev-line-title">
              <input type="text" value={l.title} onChange={set(i, 'title')} placeholder="Cruise hire, dinner for 80, DJ" aria-label={`Line ${i + 1} item`} aria-invalid={err.title ? true : undefined} autoComplete="off" />
              {err.title && <span className="field-error">{err.title}</span>}
            </div>
            <div>
              <input type="number" inputMode="decimal" step="1" value={l.unit_amount} onChange={set(i, 'unit_amount')} placeholder="0" aria-label={`Line ${i + 1} amount per unit`} aria-invalid={err.unit_amount ? true : undefined} style={num} />
              {err.unit_amount && <span className="field-error">{err.unit_amount}</span>}
            </div>
            <div>
              <input type="number" inputMode="decimal" min="0" step="1" value={l.quantity} onChange={set(i, 'quantity')} aria-label={`Line ${i + 1} quantity`} aria-invalid={err.quantity ? true : undefined} style={num} />
              {err.quantity && <span className="field-error">{err.quantity}</span>}
            </div>
            <div>
              <input type="number" inputMode="decimal" min="0" max="100" step="1" value={l.tax_percent} onChange={set(i, 'tax_percent')} aria-label={`Line ${i + 1} tax percent`} aria-invalid={err.tax_percent ? true : undefined} style={num} />
              {err.tax_percent && <span className="field-error">{err.tax_percent}</span>}
            </div>
            <div className="ev-line-total" style={{ ...num, fontWeight: 600, color: NAVY }}>{isBlankLine(l) ? '' : fmtMoney(lineTotal(l))}</div>
            <button type="button" className="btn btn-ghost ev-line-remove" onClick={() => remove(i)} aria-label={`Remove line ${i + 1}`}>
              <X size={15} strokeWidth={2} aria-hidden="true" />
            </button>
          </div>
        );
      })}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap', marginTop: lines.length ? 10 : 0 }}>
        <Button variant="secondary" size="sm" onClick={add}>
          <Plus size={14} strokeWidth={2} aria-hidden="true" />{lines.length ? 'Add a line' : 'Add the first line'}
        </Button>
        {totals.count > 0 && (
          <dl className="ev-lines-totals">
            <dt>Taxable value</dt><dd>{fmtMoney(totals.taxable)}</dd>
            <dt>Tax</dt><dd>{fmtMoney(totals.tax)}</dd>
            <dt className="ev-lines-grand">Agreed total</dt><dd className="ev-lines-grand">{fmtMoney(totals.total)}</dd>
          </dl>
        )}
      </div>
    </div>
  );
}
